// src/utils/documentCustomer.js — doced-8f3a61c2 (Document editor, Stage 4: the estimate page)
//
// The customer card's rules — choosing a customer, a service location and who is billed — as pure
// functions, so the new page does EXACTLY what today's estimate pop-up does (QuoteFormModal.js
// handleCustomerChange / handleServiceLocationChange / handleBillToChoiceChange /
// handleBillToThirdPartyChange and its load-time service-location sync). The pop-up keeps its own copy,
// untouched; documentCustomer.test.js lifts the pop-up's handlers from disk and fails if the two differ.
//
//   • choosing a customer snapshots their contact details onto the estimate (customerDetails) and
//     resets the service location and the bill-to — a bill-to never survives a customer switch;
//   • a customer with properties must have a service location chosen ('' = not chosen yet,
//     'main' = their main address, otherwise a property id);
//   • a property's default bill-to applies until the person touches "Bill to"; a property-contact
//     bill-to never outlives its property;
//   • bill-to blocks are built only by utils/billTo.js.

import { buildAddressString } from './address';
import { buildBillToFromProperty, buildBillToFromCustomer, normalizeBillTo } from './billTo';

/** The contact snapshot the pop-up stores as customerDetails ({} when there is no customer). */
export function customerDetailsFor(customer) {
    return customer ? {
        name: customer.name,
        email: customer.email || '',
        phone: customer.phone || '',
        address1: customer.address1 || '',
        address2: customer.address2 || '',
        city: customer.city || '',
        state: customer.state || '',
        zip: customer.zip || '',
        country: customer.country || '',
    } : {};
}

export const hasProperties = (customer) => (customer?.properties || []).length > 0;

/**
 * A customer was chosen (customerId '' = cleared). → { quoteData, serviceLocation, pendingThirdParty,
 * billToTouched } — the pop-up's handleCustomerChange.
 */
export function chooseCustomer(quoteData, customerId, customers) {
    const customer = (customers || []).find((c) => c.id === customerId);
    return {
        quoteData: {
            ...quoteData,
            customerId,
            customerName: customer ? customer.name : '',
            customerDetails: customerDetailsFor(customer),
            propertyId: null, propertyName: null, serviceAddress: null,
            billTo: null,
        },
        serviceLocation: hasProperties(customer) ? '' : 'main',
        pendingThirdParty: false,
        billToTouched: false,
    };
}

/**
 * The service location on opening (the pop-up's load-time sync): the stored property, else '' when the
 * estimate was opened WITH a customer record that has properties (new from a customer, a lead, a
 * re-quote), else 'main'. A saved estimate with no property opens as 'main', exactly as in the pop-up.
 */
export function initialServiceLocation(editingQuote) {
    if (!editingQuote) return '';
    const preHasProps = (editingQuote.customer?.properties || []).length > 0;
    return editingQuote.propertyId ? editingQuote.propertyId : (preHasProps ? '' : 'main');
}

/** The pop-up's billToTouched seed: true when the loaded estimate already carries a deliberate bill-to. */
export const initialBillToTouched = (editingQuote) => !!normalizeBillTo(editingQuote?.billTo);

/**
 * A service location was chosen. → { quoteData, serviceLocation, pendingThirdParty? } (pendingThirdParty
 * is reset to false only when the person hasn't touched "Bill to") — handleServiceLocationChange.
 */
export function chooseServiceLocation(quoteData, value, customers, billToTouched) {
    const customer = (customers || []).find((c) => c.id === quoteData.customerId);
    const prop = (value && value !== 'main') ? (customer?.properties || []).find((p) => p.id === value) : null;
    let billTo = quoteData.billTo;
    if (!billToTouched) {
        const def = prop?.defaultBillTo;
        if (def?.mode === 'propertyContact') billTo = buildBillToFromProperty(prop, customer);
        else if (def?.mode === 'thirdParty') billTo = buildBillToFromCustomer((customers || []).find((c) => c.id === def.thirdPartyCustomerId));
        else billTo = null;
    } else if (billTo?.mode === 'propertyContact' && (!prop || prop.id !== billTo.propertyId)) {
        billTo = null;
    }
    const out = {
        quoteData: {
            ...quoteData,
            propertyId: prop ? prop.id : null,
            propertyName: prop ? (prop.name || '') : null,
            serviceAddress: prop ? (prop.addressCanonical || buildAddressString(prop)) : null,
            billTo,
        },
        serviceLocation: value,
    };
    if (!billToTouched) out.pendingThirdParty = false;
    return out;
}

/** "Bill to" choice: 'main' | 'propertyContact' | 'thirdParty'. → { quoteData, pendingThirdParty, billToTouched: true } */
export function chooseBillTo(quoteData, choice, customers) {
    if (choice === 'thirdParty') {
        return { quoteData: { ...quoteData, billTo: quoteData.billTo?.mode === 'thirdParty' ? quoteData.billTo : null }, pendingThirdParty: true, billToTouched: true };
    }
    if (choice === 'propertyContact') {
        const cust = (customers || []).find((c) => c.id === quoteData.customerId);
        const p = (cust?.properties || []).find((x) => x.id === quoteData.propertyId);
        return { quoteData: { ...quoteData, billTo: buildBillToFromProperty(p, cust) }, pendingThirdParty: false, billToTouched: true };
    }
    return { quoteData: { ...quoteData, billTo: null }, pendingThirdParty: false, billToTouched: true };
}

/** The other customer to bill was chosen ('' = none yet). */
export function chooseThirdParty(quoteData, customerId, customers) {
    const third = (customers || []).find((c) => c.id === customerId);
    return { quoteData: { ...quoteData, billTo: buildBillToFromCustomer(third) }, pendingThirdParty: !third, billToTouched: true };
}

/** The "Bill to" select's value. */
export const billToChoiceValue = (quoteData, pendingThirdParty) => (pendingThirdParty ? 'thirdParty' : (quoteData.billTo?.mode || 'main'));

/** The service-location options: [{ value, label }] (empty when the customer has no properties). */
export function serviceLocationOptions(customer) {
    const props = customer?.properties || [];
    if (!props.length) return [];
    return [
        { value: 'main', label: `Main address${customer ? ` — ${buildAddressString(customer)}` : ''}` },
        ...props.map((p) => ({ value: p.id, label: `${p.name || p.address1 || 'Unnamed property'} — ${p.addressCanonical || buildAddressString(p)}` })),
    ];
}

/** The address line the customer card shows: the chosen property's, else the customer's main address. */
export function cardAddress(quoteData, customer) {
    if (quoteData.propertyId && quoteData.serviceAddress) return quoteData.serviceAddress;
    const d = quoteData.customerDetails || {};
    return buildAddressString(customer && customer.id === quoteData.customerId ? customer : d) || '';
}
